import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';

import Carousel from 'components/Carousel';
import Header from 'components/Header';
import Kpi from 'components/Kpi';
import TabNavigation from 'components/TabNavigation';
import WarningMessageSmallScreen from 'components/WarningMessageSmallScreen';
import CarouselItem from 'modules/Prices/components/carouselItem';
import PricesCMS from 'modules/Prices/components/CMS';

import { usePricesKpi } from 'apiTyped/hooks/prices';
import { components } from 'apiTyped/types';

const PricesContainerDiv = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 20px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    max-width: calc(100vw - 15px);
    padding: 16px;
  }

  .filters,
  .navigation {
    margin-top: 20px;
  }

  .navigation {
    margin-bottom: 20px;
  }

  @media (min-width: 801px) {
    .surveyContent {
      line-height: 15px;
    }

    .pagination {
      margin-left: auto;
      margin-right: 13px;
      padding-left: 10px;
    }
  }

  .headerContainer {
    margin-top: 22px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  @media (max-width: 1200px) {
    .pagination {
      margin-right: auto;
    }

    .headerContainer {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .indicatorsContainer {
    background-color: ${(props) => props.theme.colors['terra-cotta']};
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 100%;
    min-height: 90px;
    width: 313px;
    min-width: 313px;
    justify-content: center;
    z-index: 1;
    border-radius: 4px;
  }

  .indicator {
    height: 60%;
    border-radius: 4px;
    padding: 8px 5px;
    width: 25%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    border: solid 0.8px rgba(255, 255, 255, 0.75);
    background-color: transparent;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 13px;
    color: white;
    text-align: center;
  }

  .indicator:not(:last-child) {
    margin-right: 13px;
  }

  .indicatorValue {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
    font-size: 20px;
  }

  .indicatorLabel {
    margin-top: 8px;
  }

  @media (max-width: 1200px) {
    .indicatorsContainer {
      width: 100%;
      justify-content: space-around;
      padding-left: 0;
      padding-right: 0;
    }
  }

  .rightFeedContainer {
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: white;
  }

  .feedClientWines {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 15px;
    margin-bottom: 5px;
    margin-left: 10px;
  }

  .feedClientWines p {
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }
`;

const NewPricesContainer = ({ children }: { children: React.ReactNode }) => {
  const { kpis } = usePricesKpi();
  const [currentKpiIndex, setCurrentKpiIndex] = useState(0);

  const kpisWeb =
    [...kpis].filter(
      (item: components['schemas']['PricesWineKpi']) =>
        item && item.network_code == 'WEB'
    ) || [];
  const currentKpiWeb =
    kpisWeb.length > 0 && kpisWeb[currentKpiIndex]
      ? kpisWeb[currentKpiIndex]
      : null;

  const updateCurrentKpi = () => {
    if (!kpisWeb || kpisWeb.length == 1) return;

    if (currentKpiIndex == kpisWeb.length - 1 || kpisWeb.length < 2) {
      setCurrentKpiIndex(0);
    } else {
      setCurrentKpiIndex(currentKpiIndex + 1);
    }
  };

  const getSubtitleKpi = (
    selectKpi: components['schemas']['PricesWineKpi'] | null
  ) => {
    if (!selectKpi || !selectKpi.wine_name) {
      return '';
    }

    return get(selectKpi, 'avg_price_recent_vintage', '0')
      ? I18n.t('prices.subTitleKpi')
      : I18n.t('averagePrice');
  };

  const getPriceFromKpi = (
    selectKpi: components['schemas']['PricesWineKpi'] | null
  ) => {
    if (!selectKpi || !selectKpi.wine_name) {
      return '-';
    }

    const avg_price_recent_vintage = get(selectKpi, 'avg_price_recent_vintage');
    if (avg_price_recent_vintage) {
      return avg_price_recent_vintage.toLocaleString('fr-FR', {
        style: 'currency',
        currency: selectKpi.displayed_currency
          ? selectKpi.displayed_currency
          : 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    const avg_price = get(selectKpi, 'avg_price') || '0';
    return avg_price.toLocaleString('fr-FR', {
      style: 'currency',
      currency: selectKpi.displayed_currency
        ? selectKpi.displayed_currency
        : 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  useEffect(() => {
    if (!kpis) {
      return;
    }

    const index = Math.floor(
      Math.random() *
        kpis.filter((item) => item && item.network_code == 'WEB').length
    );
    setCurrentKpiIndex(index);
  }, [kpis]);

  const navigationOptions = [
    {
      label: I18n.t('prices.navigationOptions.price'),
      route: '/prices/price',
      active: true
    },
    {
      label: I18n.t('prices.navigationOptions.compare'),
      route: '/prices/compare',
      active: false
    },
    {
      label: I18n.t('prices.navigationOptions.evolution'),
      route: '/prices/evolution',
      active: false
    }
  ];

  return (
    <PricesContainerDiv>
      <Header title={I18n.t('prices.title')} />
      <div className="headerContainer">
        <Kpi
          value={getPriceFromKpi(currentKpiWeb)}
          title={currentKpiWeb ? `${currentKpiWeb.wine_name} - WEB` : ''}
          handleNext={kpisWeb && kpisWeb.length > 1 ? updateCurrentKpi : null}
          subTitle={getSubtitleKpi(currentKpiWeb)}
        />
        <Carousel ItemComponent={CarouselItem} data={kpis} />
      </div>
      {window.innerWidth > 845 ? (
        <React.Fragment>
          <PricesCMS />
          <div className="navigation">
            <TabNavigation buttons={navigationOptions} />
          </div>
          {children}
        </React.Fragment>
      ) : (
        <WarningMessageSmallScreen />
      )}
    </PricesContainerDiv>
  );
};

export default NewPricesContainer;
